import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";
import { Trash2, PencilLine } from "lucide-react";
import { toast } from "react-hot-toast";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const categories = [
  "Men's Clothing",
  "Women's Clothing",
  "Kids' Clothing",
  "Shoes",
  "Bags & Accessories",
  "Clearance Sale",
];
const subCategoryOptions = {
  "Men's Clothing": [
    "All",
    "Suits",
    "Blazers",
    "Tops",
    "Bottoms",
    "Jackets & Coats",
  ],
  "Women's Clothing": ["All", "Dresses", "Tops", "Bottoms", "Jackets & Coats"],
  "Kids' Clothing": ["All", "Tops", "Bottoms"],
  Shoes: ["All"],
  "Bags & Accessories": ["All"],
  "Clearance Sale": ["All"],
};

const tags = ["New Arrival", "Bestseller", "Featured"];
const sizes = ["XS", "S", "M", "L", "XL", "XXL"];
const PAGE_SIZE = 6;

export default function ManageProducts() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    price: "",
    description: "",
    category: categories[0],
    subCategory: "All",
    tag: tags[0],
    size: "",
    image: "",
    rating: 0,
    likes: 0,
    inStock: true,
  });

  const [preview, setPreview] = useState(null);
  const [editId, setEditId] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterSubCategory, setFilterSubCategory] = useState("All");
  const [filterTag, setFilterTag] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${SERVER_URL}/api/products`);
      setProducts(res.data);
    } catch (err) {
      console.error("Failed to fetch products:", err);
      toast.error("We're having trouble loading products. Please try again.");
    }
    setLoading(false);
  };

  const applyFilters = useCallback(() => {
    let filtered = [...products];
    if (searchTerm.trim()) {
      filtered = filtered.filter((p) =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    if (filterCategory)
      filtered = filtered.filter((p) => p.category === filterCategory);
    if (filterSubCategory && filterSubCategory !== "All") {
      filtered = filtered.filter((p) => p.subCategory === filterSubCategory);
    }
    if (filterTag) filtered = filtered.filter((p) => p.tag === filterTag);
    setFilteredProducts(filtered);
  }, [products, searchTerm, filterCategory, filterTag, filterSubCategory]);

  useEffect(() => {
    applyFilters();
    setCurrentPage(1);
  }, [applyFilters]);

  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );
  const totalPages = Math.ceil(filteredProducts.length / PAGE_SIZE);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === "category") {
        return {
          ...prev,
          category: value,
          subCategory: subCategoryOptions[value]?.[0] || "All", // Reset subCategory
        };
      }

      return {
        ...prev,
        [name]:
          name === "rating"
            ? Math.min(Math.max(Number(value), 0), 5)
            : name === "likes"
              ? Math.max(Number(value), 0)
              : value,
      };
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const imgFormData = new FormData();
    imgFormData.append("image", file);

    try {
      setLoading(true);
      const res = await fetch(`${SERVER_URL}/api/upload/image`, {
        method: "POST",
        body: imgFormData,
      });
      const data = await res.json();
      if (data.success) {
        setPreview(data.imageUrl);
        setFormData((prev) => ({ ...prev, image: data.imageUrl }));
      } else toast.error("Couldn't upload image. Please try again.");
    } catch (err) {
      console.error("Image upload error:", err);
      toast.error("Couldn't upload image. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !formData.image ||
      !formData.name.trim() ||
      !formData.price ||
      formData.price <= 0
    )
      return toast.error("Please fill in all required fields");
    try {
      setLoading(true);
      if (editId) {
        await axios.put(`${SERVER_URL}/api/products/${editId}`, formData);

        toast.success("✅ Product updated!");
      } else {
        await axios.post(`${SERVER_URL}/api/products`, formData);

        toast.success("✅ Product created!");
      }
      resetForm();
      fetchProducts();
    } catch (err) {
      console.error(err);
      toast.error("Couldn't save product. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      price: "",
      description: "",
      category: categories[0],
      tag: tags[0],
      size: "",
      image: "",
      rating: 0,
      likes: 0,
    });
    setPreview(null);
    setEditId(null);
  };

  const handleEdit = (product) => {
    setFormData({
      name: product.name || "",
      price: product.price || "",
      description: product.description || "",
      category: product.category || categories[0],
      subCategory: product.subCategory || "All",
      tag: product.tag || tags[0],
      size: product.size || "",
      image: product.image || "",
      rating: product.rating || 0,
      likes: product.likes || 0,
      inStock: product.inStock ?? true,
    });
    setPreview(product.image);
    setEditId(product._id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      setLoading(true);
      await axios.delete(`${SERVER_URL}/api/products/${id}`);
      fetchProducts();
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-black text-gray-900 tracking-tight">
          Manage Products
        </h1>
        <p className="text-gray-500 mt-1">
          Add, edit, and manage your product catalog
        </p>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white border border-gray-100 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <span className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center text-white text-sm">
            {editId ? "✏" : "+"}
          </span>
          {editId ? "Edit Product" : "Add New Product"}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              Product Name *
            </label>
            <input
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
              placeholder="Enter product name"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              Price (KES) *
            </label>
            <input
              name="price"
              type="number"
              min="0"
              step="0.01"
              value={formData.price}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
              placeholder="Enter price"
              required
            />
          </div>

          <div className="md:col-span-2 space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition resize-none"
              placeholder="Enter description"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              Category *
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
              required
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              Subcategory *
            </label>
            <select
              name="subCategory"
              value={formData.subCategory}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
              required
            >
              {subCategoryOptions[formData.category]?.map((subCat) => (
                <option key={subCat} value={subCat}>
                  {subCat}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">Tag *</label>
            <select
              name="tag"
              value={formData.tag}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
              required
            >
              {tags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">Size</label>
            <select
              name="size"
              value={formData.size}
              onChange={handleChange}
              className="w-full px-4 py-3 mb-2 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
            >
              <option value="">Select Size</option>
              {sizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <input
              type="text"
              name="size"
              value={formData.size}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
              placeholder="Or enter custom size (e.g. 39, 42)"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              Rating (0–5)
            </label>
            <input
              name="rating"
              type="number"
              min="0"
              max="5"
              step="0.1"
              value={formData.rating}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">Likes</label>
            <input
              name="likes"
              type="number"
              min="0"
              value={formData.likes}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
            />
          </div>

          <div className="md:col-span-2 flex flex-wrap gap-10 items-start">
            <div className="space-y-3">
              <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                Availability
              </label>
              <label className="flex items-center gap-4 cursor-pointer group/toggle">
                <div className="relative">
                  <input
                    type="checkbox"
                    name="inStock"
                    checked={formData.inStock}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        inStock: e.target.checked,
                      }))
                    }
                    className="sr-only"
                  />
                  <div
                    className={`w-14 h-7 rounded-full transition-all duration-300 ${formData.inStock ? "bg-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.3)]" : "bg-gray-200"}`}
                  >
                    <div
                      className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-all duration-300 mt-1 ${formData.inStock ? "translate-x-8" : "translate-x-1"}`}
                    />
                  </div>
                </div>
                <div className="flex flex-col">
                  <span
                    className={`font-bold text-sm transition-colors ${formData.inStock ? "text-yellow-600" : "text-gray-500"}`}
                  >
                    {formData.inStock ? "Available" : "Sold Out"}
                  </span>
                  <span className="text-[10px] text-gray-400 font-medium">
                    Public Status
                  </span>
                </div>
              </label>
            </div>

            <div className="flex-1 space-y-3 min-w-[280px]">
              <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                Product Image *
              </label>
              <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 group-hover:border-yellow-200 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100 transition-all cursor-pointer"
                />
                {preview && (
                  <div className="relative flex-shrink-0 group/preview">
                    <img
                      src={preview}
                      alt="Product Preview"
                      className="h-20 w-20 object-cover rounded-xl border-2 border-white shadow-xl group-hover/preview:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover/preview:opacity-100 rounded-xl transition-opacity pointer-events-none" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          {editId && (
            <button
              type="button"
              onClick={resetForm}
              className="px-6 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition"
            >
              Cancel Edit
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-8 py-2.5 rounded-xl bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-bold hover:from-yellow-600 hover:to-yellow-700 shadow-lg shadow-yellow-500/20 transition disabled:opacity-50"
          >
            {editId ? "Update Product" : "Add Product"}
          </button>
        </div>
      </form>

      {/* Filters & Search */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4 flex flex-col md:flex-row gap-3 shadow-sm">
        <input
          type="search"
          placeholder="Search by product name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-grow px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
        />
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition"
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
        <select
          value={filterSubCategory}
          onChange={(e) => setFilterSubCategory(e.target.value)}
          className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition"
        >
          <option value="">All Sub-Categories</option>
          {subCategoryOptions[formData.category]?.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
        <select
          value={filterTag}
          onChange={(e) => setFilterTag(e.target.value)}
          className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition"
        >
          <option value="">All Tags</option>
          {tags.map((tag) => (
            <option key={tag} value={tag}>
              {tag}
            </option>
          ))}
        </select>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center items-center h-48">
            <CircularProgress sx={{ color: "#eab308" }} />
          </div>
        ) : paginatedProducts.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <p className="text-gray-400 text-lg font-medium">
              No products found.
            </p>
            <p className="text-gray-300 text-sm mt-1">
              Try adjusting your search or filters.
            </p>
          </div>
        ) : (
          paginatedProducts.map((product) => (
            <div
              key={product._id}
              className="bg-white border border-gray-100 rounded-[1.5rem] shadow-sm hover:shadow-[0_12px_40px_rgb(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-500 overflow-hidden group"
            >
              <div className="relative h-44 overflow-hidden bg-gray-50">
                <img
                  src={product.image}
                  alt={product.name}
                  className="h-full w-full object-contain group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-3 left-3 flex flex-col gap-1.5 transform -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-500">
                  <span
                    className={`text-[9px] font-black tracking-tighter px-2 py-0.5 rounded shadow-sm ${product.inStock ? "bg-green-500 text-white" : "bg-red-500 text-white"}`}
                  >
                    {product.inStock ? "STOCK-IN" : "OUT"}
                  </span>
                  {product.tag && (
                    <span className="bg-yellow-500 text-white text-[9px] font-black tracking-tighter px-2 py-0.5 rounded shadow-sm">
                      {product.tag.toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              <div className="p-4 px-5">
                <div className="flex justify-between items-start mb-1">
                  <h3 className="text-sm font-black text-gray-900 group-hover:text-yellow-600 transition-colors truncate pr-4">
                    {product.name}
                  </h3>
                  <span className="text-xs font-bold text-gray-400 bg-gray-50 px-2 py-0.5 rounded-lg whitespace-nowrap">
                    {product.category.split("'")[0]}
                  </span>
                </div>
                <p className="text-yellow-600 font-black text-base mb-3 leading-none">
                  KES {Number(product.price).toLocaleString()}
                </p>

                <p className="text-xs text-gray-400 font-medium line-clamp-2 mb-4 h-8 leading-relaxed italic">
                  "{product.description}"
                </p>

                <div className="flex items-center gap-4 text-[10px] font-black text-gray-300 uppercase tracking-widest mb-4">
                  <span className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />{" "}
                    {product.subCategory}
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-200" /> ⭐{" "}
                    {product.rating?.toFixed(1)}
                  </span>
                </div>

                <div className="flex gap-2.5">
                  <button
                    onClick={() => handleEdit(product)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl bg-gray-50 text-gray-700 font-bold hover:bg-yellow-500 hover:text-white transition-all text-xs"
                  >
                    <PencilLine size={13} /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(product._id)}
                    className="p-2 rounded-xl border-2 border-transparent text-gray-300 hover:text-red-500 hover:bg-red-50 hover:border-red-100 transition-all"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 pb-4">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition font-medium text-sm"
          >
            ← Prev
          </button>

          {[...Array(totalPages)].map((_, idx) => {
            const pageNum = idx + 1;
            return (
              <button
                key={pageNum}
                onClick={() => goToPage(pageNum)}
                className={`w-10 h-10 rounded-xl font-bold text-sm transition ${
                  currentPage === pageNum
                    ? "bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-lg shadow-yellow-500/20"
                    : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition font-medium text-sm"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
